import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { Effect, GameEvent, PlayerView } from '@estandarte/engine';
import { icons, sigilOf } from '../art';
import { MapBase, MapTerrain } from './MapTerrain';
import { bossName, guildHex, inkOn, theme } from '../theme';

const INK = '#23263A';
const SEA = '#3F6E8C';
const DANGER = '#A23B3B';
const PAPER = '#F6F6F1';

export type TargetKind = 'attack' | 'move' | 'own';

export interface MapProps {
  view: PlayerView | null;
  selected?: string | null;
  target?: string | null;
  targets?: Set<string>;
  targetKind?: TargetKind;
  clickable?: Set<string>;
  /** Territórios marcados (ex.: alvos já escolhidos da Great Fireball). */
  marked?: Set<string>;
  bossTargetable?: boolean;
  bossSelected?: boolean;
  onTerritory?: (id: string) => void;
  onLair?: (region: string) => void;
  events?: GameEvent[];
  eventSeq?: number;
  title?: string;
  /** Nome e vida do boss desenhados no mapa (desligado quando a tela mostra o card do boss). */
  bossLabel?: boolean;
}

const territoryIds = Object.keys(theme.territories);

/** Curva da rota de barco: controle = ponto médio + perpendicular * bend. */
function seaPath(a: { x: number; y: number }, b: { x: number; y: number }, bend: number): string {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const cx = mx - (b.y - a.y) * bend;
  const cy = my + (b.x - a.x) * bend;
  return `M${a.x},${a.y} Q${cx},${cy} ${b.x},${b.y}`;
}

/** Ponto do meio da ligação (no meio da curva, para barcos). */
function linkMid(a: { x: number; y: number }, b: { x: number; y: number }, bend: number) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  return { x: mx - (b.y - a.y) * bend * 0.5, y: my + (b.x - a.x) * bend * 0.5 };
}

/** Posição do nome conforme `labelPos`. */
function labelAt(pos: string): { x: number; y: number; anchor: 'middle' | 'start' | 'end' } {
  switch (pos) {
    case 'b':
      return { x: 0, y: 34, anchor: 'middle' };
    case 'r':
      return { x: 34, y: 20, anchor: 'start' };
    case 'l':
      return { x: -18, y: 4, anchor: 'end' };
    default:
      return { x: 0, y: -24, anchor: 'middle' };
  }
}

const Defs = memo(function Defs() {
  return (
    <defs>
      <filter id="ink" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="1" seed="5" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="1.4" xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <marker id="arrow-head" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 L2.5,5 Z" fill={DANGER} />
      </marker>
    </defs>
  );
});

/** Camada estática: arte do mapa, ligações e templos. */
const StaticLayer = memo(function StaticLayer() {
  return (
    <g>
      <MapBase />
      <MapTerrain />
      <g filter="url(#ink)">
        {theme.links.map((l) => {
          const a = theme.territories[l.a];
          const b = theme.territories[l.b];
          if (l.sea)
            return (
              <path
                key={`${l.a}-${l.b}`}
                d={seaPath(a, b, l.bend)}
                fill="none"
                stroke={SEA}
                strokeWidth={1.7}
                strokeDasharray="6 5"
                strokeLinecap="round"
                opacity={0.9}
              />
            );
          if (l.kind === 'bridge')
            return (
              <g key={`${l.a}-${l.b}`} stroke={INK} strokeWidth={1.2} opacity={0.75}>
                <line x1={a.x - 4} y1={a.y} x2={b.x - 4} y2={b.y} />
                <line x1={a.x + 4} y1={a.y} x2={b.x + 4} y2={b.y} />
              </g>
            );
          return (
            <line
              key={`${l.a}-${l.b}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={INK}
              strokeWidth={1.1}
              strokeDasharray={l.kind === 'ice' ? '2 4' : undefined}
              strokeLinecap="round"
              opacity={0.5}
            />
          );
        })}
        {Object.entries(theme.lairs).map(([region, lair]) =>
          lair.adjacent.map((t) => {
            const p = theme.territories[t];
            return (
              <line
                key={`${region}-${t}`}
                x1={lair.x}
                y1={lair.y}
                x2={p.x}
                y2={p.y}
                stroke={DANGER}
                strokeWidth={1.2}
                strokeDasharray="1.5 4"
                strokeLinecap="round"
                opacity={0.6}
              />
            );
          }),
        )}
      </g>
      {territoryIds
        .filter((id) => theme.territories[id].temple)
        .map((id) => {
          const t = theme.territories[id];
          return <image key={`temple-${id}`} href={icons.temple} x={t.x - 30} y={t.y + 2} width={15} height={15} opacity={0.85} aria-hidden />;
        })}
    </g>
  );
});

function Lair(props: {
  region: string;
  boss: PlayerView['boss'];
  targetable: boolean;
  selected: boolean;
  label: boolean;
  onClick?: () => void;
}) {
  const { region, boss, targetable, selected, onClick, label } = props;
  const lair = theme.lairs[region];
  const active = !!boss;
  const r = active ? 17 : 11;
  const color = boss ? theme.bosses[boss.id]?.color ?? DANGER : DANGER;
  return (
    <g
      className={`lair${targetable ? ' clickable' : ''}`}
      transform={`translate(${lair.x},${lair.y})`}
      onClick={targetable ? onClick : undefined}
      role={targetable ? 'button' : undefined}
      aria-label={boss ? `Covil com ${bossName(boss.id)}, vida ${boss.hp} de ${boss.maxHp}` : `Covil de ${theme.regions[region].name}`}
      tabIndex={targetable ? 0 : undefined}
      onKeyDown={(e) => {
        if (targetable && (e.key === 'Enter' || e.key === ' ')) onClick?.();
      }}
    >
      {targetable && <circle r={r + 9} fill="none" stroke={DANGER} strokeWidth={2.5} className="pulse" />}
      {selected && <circle r={r + 7} fill="none" stroke={INK} strokeWidth={2.5} />}
      {active ? (
        <>
          <circle r={r + 3} fill={color} opacity={0.55} filter="url(#ink)" />
          <circle r={r} fill="#2B2D3D" stroke={DANGER} strokeWidth={1.6} />
          <image href={sigilOf(boss.id)} x={-r + 2} y={-r + 2} width={(r - 2) * 2} height={(r - 2) * 2} />
          {label && (
            <>
              <text className="tname" y={r + 15} textAnchor="middle" fontSize={14} fill={DANGER}>
                {bossName(boss.id)}
              </text>
              <g transform={`translate(0, ${r + 20})`}>
                <rect x={-26} y={0} width={52} height={15} rx={3} fill={PAPER} stroke={DANGER} strokeWidth={1} />
                <image href={icons.heart} x={-23} y={2} width={11} height={11} />
                <text x={-9} y={11.5} fontSize={11} fontWeight={700} fill={DANGER} className="num">
                  {boss.hp}/{boss.maxHp}
                </text>
                <text x={23} y={11.5} fontSize={10} fill={INK} textAnchor="end" className="num">
                  ⧗{boss.timer}
                </text>
              </g>
            </>
          )}
        </>
      ) : (
        <>
          <circle r={r} fill={PAPER} stroke={DANGER} strokeWidth={1.3} opacity={0.9} />
          <image href={icons.horns} x={-r + 3} y={-r + 3} width={(r - 3) * 2} height={(r - 3) * 2} opacity={0.75} />
        </>
      )}
    </g>
  );
}

/** Peça da tropa: bandeirinha na cor da guilda com o número ao lado. */
function Piece({ color, troops }: { color: string | null; troops: number | null }) {
  const fill = color ?? '#D8D8D0';
  return (
    <g filter="url(#ink)">
      <ellipse cx={-5} cy={14} rx={9} ry={2.6} fill={INK} opacity={0.18} />
      <line x1={-5} y1={14} x2={-5} y2={-17} stroke={INK} strokeWidth={1.8} strokeLinecap="round" />
      <path d="M-5,-17 H15 L10,-10.5 L15,-4 H-5 Z" fill={fill} stroke={INK} strokeWidth={1.3} strokeLinejoin="round" />
      {troops !== null && (
        <g transform="translate(2,-1)">
          <rect x={0} y={0} width={troops >= 10 ? 26 : 19} height={17} rx={3} fill={PAPER} stroke={INK} strokeWidth={1.2} />
          <text
            key={troops}
            className="troops pop"
            x={(troops >= 10 ? 26 : 19) / 2}
            y={13}
            textAnchor="middle"
            fontSize={troops >= 100 ? 11 : 14}
            fill={INK}
          >
            {troops}
          </text>
        </g>
      )}
    </g>
  );
}

/** Ícones dos efeitos ativos sobre um território. */
function EffectBadges({ effects }: { effects: Effect[] }) {
  if (!effects.length) return null;
  const glyph: Record<string, { ch: string; color: string; label: string }> = {
    blessing: { ch: '✚', color: '#9C7A1D', label: 'Bênção' },
    ward: { ch: '◆', color: '#585C6E', label: 'Stone Skin' },
    paralyze: { ch: 'Z', color: '#6B3B6E', label: 'Paralisado' },
    frozen: { ch: '❄', color: '#3F6E8C', label: 'Congelado' },
  };
  const list = effects.filter((e) => glyph[e.kind]);
  return (
    <g transform="translate(-17,-12)">
      {list.map((e, i) => (
        <g key={e.id} transform={`translate(0,${i * 14})`} aria-label={glyph[e.kind].label}>
          <title>{glyph[e.kind].label}</title>
          <circle r={6.5} fill={PAPER} stroke={glyph[e.kind].color} strokeWidth={1.2} />
          <text y={3.5} textAnchor="middle" fontSize={9} fontWeight={700} fill={glyph[e.kind].color}>
            {glyph[e.kind].ch}
          </text>
        </g>
      ))}
    </g>
  );
}

interface Floater {
  id: number;
  territory: string;
  delta: number;
}

let floaterSeq = 0;

/**
 * Compara as tropas e os donos com a última visão: gera os números que sobem (+3, −2)
 * e marca os territórios que trocaram de dono, para a bandeira nova ser fincada.
 */
function useTroopChanges(view: PlayerView | null) {
  const prev = useRef<Record<string, { owner: number; troops: number }> | null>(null);
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const [planted, setPlanted] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!view) return;
    const now: Record<string, { owner: number; troops: number }> = {};
    for (const [id, t] of Object.entries(view.territories)) now[id] = { owner: t.owner, troops: t.troops };
    const before = prev.current;
    prev.current = now;
    if (!before) return;
    const fresh: Floater[] = [];
    const plant: Record<string, number> = {};
    for (const [id, t] of Object.entries(now)) {
      const b = before[id];
      if (!b) continue;
      if (b.owner !== t.owner) plant[id] = ++floaterSeq;
      const delta = b.owner === t.owner ? t.troops - b.troops : 0;
      if (delta !== 0) fresh.push({ id: ++floaterSeq, territory: id, delta });
    }
    if (Object.keys(plant).length) setPlanted((p) => ({ ...p, ...plant }));
    if (!fresh.length) return;
    setFloaters((f) => [...f, ...fresh].slice(-30));
    const ids = new Set(fresh.map((f) => f.id));
    const t = setTimeout(() => setFloaters((f) => f.filter((x) => !ids.has(x.id))), 1500);
    return () => clearTimeout(t);
  }, [view]);
  return { floaters, planted };
}

export function MapView(props: MapProps) {
  const { view, selected, target, targets, targetKind, clickable, marked, onTerritory, events, eventSeq } = props;
  const [w, h] = theme.viewBox;
  const { floaters, planted } = useTroopChanges(view);

  // Territórios envolvidos na última ação, para um brilho rápido.
  const flashes = useMemo(() => {
    const out = new Map<string, string>();
    for (const e of events ?? []) {
      if (e.type === 'conquest') out.set(e.to, INK);
      else if (e.type === 'combat') {
        if (!out.has(e.to)) out.set(e.to, DANGER);
      } else if (e.type === 'bossRaid') {
        for (const l of e.losses) if (l.lost) out.set(l.territory, DANGER);
        for (const t of e.frozen) out.set(t, SEA);
      } else if (e.type === 'power') out.set(e.target, DANGER);
      else if (e.type === 'heal') out.set(e.territory, '#2F6D3A');
      else if (e.type === 'lootPlayed') {
        for (const hit of e.hits ?? []) out.set(hit.territory, DANGER);
        if (e.territory && !e.hits) out.set(e.territory, '#9C7A1D');
      }
    }
    return out;
  }, [events]);

  const lastCombat = useMemo(() => {
    const list = (events ?? []).filter((e) => e.type === 'combat' || e.type === 'combatPreview');
    const e = list[list.length - 1];
    if (!e || !('to' in e) || !e.to) return null;
    return { from: e.from, to: e.to };
  }, [events]);

  const walls = (view?.effects ?? []).filter((e) => e.kind === 'wall' && e.link);
  const ringColor = targetKind === 'attack' ? DANGER : targetKind === 'move' ? SEA : INK;

  return (
    <svg className="map" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={props.title ?? 'Mapa de campanha'}>
      <Defs />
      <StaticLayer />

      {walls.map((e) => {
        const [a, b] = e.link!;
        const link = theme.links.find((l) => (l.a === a && l.b === b) || (l.a === b && l.b === a));
        const m = linkMid(theme.territories[a], theme.territories[b], link?.sea ? link.bend : 0);
        const ang = (Math.atan2(theme.territories[b].y - theme.territories[a].y, theme.territories[b].x - theme.territories[a].x) * 180) / Math.PI;
        return (
          <g key={`wall-${e.id}`} transform={`translate(${m.x},${m.y}) rotate(${ang + 90})`} aria-label="Magic Wall">
            <title>Magic Wall</title>
            <rect x={-12} y={-4} width={24} height={8} fill="#8A3B2E" stroke={INK} strokeWidth={1.2} rx={1.5} />
            <path d="M-4,-4 V4 M4,-4 V4 M-12,0 H12" stroke={INK} strokeWidth={0.8} opacity={0.6} />
          </g>
        );
      })}

      {lastCombat &&
        (() => {
          const A = theme.territories[lastCombat.from];
          const B = theme.territories[lastCombat.to];
          const len = Math.hypot(B.x - A.x, B.y - A.y) || 1;
          const ux = (B.x - A.x) / len;
          const uy = (B.y - A.y) / len;
          return (
            <line
              key={`arrow-${eventSeq}`}
              className="attack-arrow"
              x1={A.x + ux * 14}
              y1={A.y + uy * 14}
              x2={B.x - ux * 24}
              y2={B.y - uy * 24}
              stroke={DANGER}
              strokeWidth={3.2}
              strokeLinecap="round"
              strokeDasharray="9 6"
              markerEnd="url(#arrow-head)"
              pointerEvents="none"
            />
          );
        })()}

      {Object.keys(theme.lairs).map((region) => {
        const boss = view?.boss && view.boss.region === region ? view.boss : null;
        return (
          <Lair
            key={region}
            region={region}
            boss={boss}
            targetable={!!boss && !!props.bossTargetable}
            selected={!!boss && !!props.bossSelected}
            label={props.bossLabel !== false}
            onClick={() => props.onLair?.(region)}
          />
        );
      })}

      {territoryIds.map((id) => {
        const t = theme.territories[id];
        const st = view?.territories[id];
        const owner = st ? view!.players[st.owner] : null;
        const isTarget = targets?.has(id) ?? false;
        const isClickable = clickable?.has(id) ?? false;
        const isSelected = selected === id;
        const flash = flashes.get(id);
        const label = labelAt(t.labelPos);
        const effects = (view?.effects ?? []).filter((e) => e.territory === id);
        return (
          <g
            key={id}
            className={`terr${isClickable ? ' clickable' : ''}`}
            transform={`translate(${t.x},${t.y})`}
            onClick={isClickable ? () => onTerritory?.(id) : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            aria-label={`${t.name}${owner ? `, ${owner.name}, ${st!.troops} tropas` : ''}`}
            onKeyDown={(e) => {
              if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onTerritory?.(id);
              }
            }}
          >
            <circle r={24} fill="transparent" />
            {flash && <circle key={`f-${eventSeq}`} r={24} fill="none" stroke={flash} strokeWidth={4} className="flash" />}
            {isTarget && (
              <circle r={25} fill="none" stroke={ringColor} strokeWidth={2.6} strokeDasharray={targetKind === 'own' ? '3 3' : undefined} className="pulse" />
            )}
            {(isSelected || target === id || marked?.has(id)) && (
              <circle r={24} fill={marked?.has(id) ? 'rgba(162,59,59,0.12)' : 'none'} stroke={target === id || marked?.has(id) ? DANGER : INK} strokeWidth={3} />
            )}
            {st ? (
              <g key={`p-${planted[id] ?? 0}`} className={planted[id] ? 'piece plant' : 'piece'}>
                <Piece color={owner ? guildHex(owner.color) : null} troops={st.troops} />
              </g>
            ) : (
              <circle r={3.5} fill={INK} opacity={0.7} />
            )}
            <EffectBadges effects={effects} />
            <text className="tname" x={label.x} y={label.y} textAnchor={label.anchor} fontSize={14} fill={INK}>
              {t.name}
            </text>
          </g>
        );
      })}

      <g className="floaters" pointerEvents="none" aria-hidden>
        {floaters.map((f) => {
          const t = theme.territories[f.territory];
          return (
            <text key={f.id} className={`floater ${f.delta > 0 ? 'up' : 'down'}`} x={t.x + 18} y={t.y - 20} textAnchor="middle">
              {f.delta > 0 ? `+${f.delta}` : `−${-f.delta}`}
            </text>
          );
        })}
      </g>
    </svg>
  );
}

export { inkOn };
